import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import BottomNav from "../components/BottomNav";
import { getMatches } from "../services/matchService";
import API from "../services/api";
import { getUser } from '../utils/storage';
import { getTeamLogo } from "../configs/assets";
import { parseMatchTime } from "../utils/dateUtils";

const CATEGORIES = [
  { id: "SIXES", label: "Most 6s", icon: "🏏", color: "bg-theme-card", text: "text-blue-500" },
  { id: "FOURS", label: "Most 4s", icon: "🎯", color: "bg-theme-card", text: "text-emerald-500" },
  { id: "WICKETS", label: "Wickets", icon: "🔥", color: "bg-theme-card", text: "text-red-500" },
  { id: "CATCHES", label: "Catches", icon: "🧤", color: "bg-theme-card", text: "text-purple-500" },
  { id: "RUNOUTS", label: "Run Outs", icon: "🏃", color: "bg-theme-card", text: "text-orange-500" },
  { id: "MILESTONES", label: "50s / 100s", icon: "💯", color: "bg-theme-card", text: "text-pink-500" }
];

const PlayerPerformance = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [showPlayerModal, setShowPlayerModal] = useState(false);

  const [players, setPlayers] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [prediction, setPrediction] = useState(1);
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [activeRole, setActiveRole] = useState("WK");
  const hasFetched = useRef(false);

  const ROLES = [
    { id: "WK", label: "WK" },
    { id: "BAT", label: "BAT" },
    { id: "AR", label: "ALL" },
    { id: "BOWL", label: "BOWL" }
  ];

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    console.log("🏆 PlayerPerformance Mounted");
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const res = await getMatches(1);
      const res2 = await getMatches(2);
      const res3 = await getMatches(3);
      const allData = [...(res.data || []), ...(res2.data || []), ...(res3.data || [])];

      // Show ONLY upcoming matches (exclude Live and Completed)
      const activeMatches = allData.filter(m => 
        m && 
        m.status?.toLowerCase() !== "completed" && 
        m.status?.toLowerCase() !== "live" &&
        m.status !== 1 // Some APIs use 1 for live
      );
      setMatches(activeMatches.slice(0, 15));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (cat) => {
    setSelectedCategory(cat);
    setShowMatchModal(true);
  };

  const handleMatchSelect = async (match) => {
    setSelectedMatch(match);
    setShowMatchModal(false);
    setShowPlayerModal(true);
    setLoading(true);
    try {
      const matchId = match.match_id || match.id;
      const user = getUser();

      setPlayers([]); // Clear previous
      setChallenges([]);

      // Decouple fetching so players can load even if challenges are slow
      import("../services/playerService").then(({ getPlayers }) => {
        getPlayers(String(matchId))
          .then(res => {
            const fetchedPlayers = res.data || [];
            setPlayers(fetchedPlayers);
            
            // ✅ Smart Role Selection: If 'WK' has no players, switch to first available role
            const roles = ["WK", "BAT", "AR", "BOWL"];
            const currentRoleCount = fetchedPlayers.filter(p => (p.role || "").toUpperCase().includes(activeRole)).length;
            
            if (currentRoleCount === 0) {
              for (const r of roles) {
                const count = fetchedPlayers.filter(p => (p.role || "").toUpperCase().includes(r)).length;
                if (count > 0) {
                  setActiveRole(r);
                  break;
                }
              }
            }
          })
          .catch(err => console.error("Player load failed:", err));
      });

      API.get(`/challenges/${matchId}?userId=${user.id || 0}`)
        .then(res => setChallenges(res.data.challenges || []))
        .catch(err => console.error("Challenge load failed:", err));

    } catch (err) {
      console.error("Selection error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerSelect = (player) => {
    navigate("/player-contests", {
      state: { match: selectedMatch, category: selectedCategory, player, challenges }
    });
  };

  const filteredPlayers = (players || []).filter(p => {
    if (!p) return false;
    const r = (p.role || "").toUpperCase();
    if (activeRole === "AR") return r.includes("AR") || r.includes("ALL");
    if (activeRole === "WK") return r.includes("WK") || r.includes("WICKET");
    if (activeRole === "BAT") return r.includes("BAT");
    if (activeRole === "BOWL") return r.includes("BOWL");
    return r === activeRole;
  }).sort((a, b) => {
    if (!a || !b) return 0;
    if (selectedMatch?.lineup_announced) {
       return (Number(b.is_playing) || 0) - (Number(a.is_playing) || 0);
    }
    return 0;
  });

  return (
    <div className="bg-theme-bg text-theme-text min-h-screen pb-20 relative font-sans">
      <div className="flex items-center p-4 sticky top-0 bg-theme-card border-b border-theme-border z-50 shadow-theme-sm">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center bg-theme-bg rounded-xl border border-theme-border text-theme-text transition-all active:scale-95 shadow-theme-sm"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h1 className="text-lg font-bold tracking-tight uppercase flex-1 text-center pr-10">Player Performance</h1>
      </div>

      <div className="p-5">
        <div className="relative rounded-[32px] p-8 overflow-hidden shadow-theme-lg border border-theme-border bg-theme-card">
          <div className="relative z-10">
            <h2 className="text-3xl font-bold tracking-tighter text-theme-text mb-4 uppercase leading-[0.9]">Win on every<br /><span className="text-accent underline decoration-4 underline-offset-4">Performance!</span></h2>
            <p className="text-theme-text-sec text-xs mb-6 max-w-[280px] leading-relaxed opacity-60">Ab sirf team banake nahi, <b className="text-theme-text">Players ke counts</b> pe predict karo aur jeeto!</p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-accent/10 text-accent px-3 py-1.5 rounded-lg text-[8px] font-bold uppercase tracking-widest border border-accent/20">🚀 World's First</span>
              <span className="bg-accent text-white px-3 py-1.5 rounded-lg text-[8px] font-bold uppercase tracking-widest shadow-sm">🔥 Win Big</span>
            </div>
          </div>
          <div className="absolute right-[-20px] bottom-[-20px] opacity-10 transform -rotate-12"><span className="text-9xl">🏆</span></div>
        </div>
      </div>

      <div className="px-5">
        <div className="grid grid-cols-2 gap-4">
          {CATEGORIES.map(cat => (
            <div
              key={cat.id}
              onClick={() => handleCategoryClick(cat)}
              className="relative rounded-[24px] p-5 cursor-pointer border border-theme-border active:scale-95 transition-all bg-theme-card shadow-theme-sm group"
            >
              <div className="flex flex-col h-full justify-between items-start">
                <span className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300">{cat.icon}</span>
                <div>
                  <h4 className="font-bold text-theme-text text-sm uppercase tracking-tight">{cat.label}</h4>
                  <p className="text-[9px] text-theme-text-sec mt-1 uppercase font-bold tracking-widest opacity-40">Predict & Win</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 📖 HOW TO PLAY SECTION */}
      <div className="px-5 mt-10 mb-8">
        <h3 className="text-theme-text-sec text-[10px] font-bold mb-5 tracking-widest uppercase flex items-center opacity-60">
          <span className="w-6 h-px bg-theme-border mr-3"></span> How It Works
        </h3>

        <div className="bg-theme-card rounded-[24px] p-6 border border-theme-border shadow-theme-sm">
          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-theme-bg text-theme-text flex items-center justify-center font-bold text-xs shrink-0 border border-theme-border shadow-theme-sm">1</div>
              <div>
                <h4 className="text-theme-text font-bold text-xs uppercase tracking-tight mb-1">Pick a Category</h4>
                <p className="text-[10px] text-theme-text-sec leading-relaxed opacity-60 font-medium">Choose a metric you want to predict from the various cards listed above.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-theme-bg text-theme-text flex items-center justify-center font-bold text-xs shrink-0 border border-theme-border shadow-theme-sm">2</div>
              <div>
                <h4 className="text-theme-text font-bold text-xs uppercase tracking-tight mb-1">Select Match & Player</h4>
                <p className="text-[10px] text-theme-text-sec leading-relaxed opacity-60 font-medium">Select an upcoming match and the player you feel will outperform others.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-theme-bg text-theme-text flex items-center justify-center font-bold text-xs shrink-0 border border-theme-border shadow-theme-sm">3</div>
              <div>
                <h4 className="text-theme-text font-bold text-xs uppercase tracking-tight mb-1">Predict & Win Cash</h4>
                <p className="text-[10px] text-theme-text-sec leading-relaxed opacity-60 font-medium">Enter your predicted count for tie-breaker. Join the contest and win real cash!</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showMatchModal && (
        <div className="fixed inset-0 z-[100] flex justify-center items-end bg-theme-bg/80 backdrop-blur-sm p-0">
          <div className="bg-theme-card w-full max-h-[85vh] rounded-t-[32px] overflow-hidden shadow-theme-lg border-t-4 border-accent animate-[slideUp_0.3s_ease-out] flex flex-col">
            <div className="p-4 border-b border-theme-border flex items-center bg-theme-card sticky top-0 z-50 shrink-0">
              <button
                onClick={() => setShowMatchModal(false)}
                className="w-10 h-10 flex items-center justify-center bg-theme-bg rounded-xl border border-theme-border text-theme-text transition-all active:scale-95 shadow-theme-sm mr-4"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
              <div>
                <p className="text-[9px] text-theme-text-sec font-bold uppercase tracking-widest opacity-60">{selectedCategory?.label}</p>
                <h3 className="text-sm font-bold text-theme-text uppercase tracking-tight">Select Match</h3>
              </div>
            </div>
            <div className="p-4 overflow-y-auto space-y-4">
              {loading ? (
                <div className="text-center py-10 text-[10px] font-bold text-theme-text-sec uppercase tracking-widest">Loading...</div>
              ) : matches.length === 0 ? (
                <div className="text-center py-10 text-[10px] font-bold text-theme-text-sec uppercase tracking-widest">No Active Matches</div>
              ) : matches.map(match => (
                <div key={match.match_id} onClick={() => handleMatchSelect(match)} className="bg-theme-bg border border-theme-border p-4 rounded-xl flex flex-col justify-between items-start transition-all active:scale-[0.98] active:bg-theme-card shadow-theme-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-[8px] px-2 py-0.5 rounded font-bold uppercase tracking-widest shadow-sm ${match.status === 1 || match.status?.toLowerCase() === 'live' ? 'bg-green-500 text-white' : 'bg-accent text-white'}`}>{match.status === 1 ? 'LIVE' : (match.status || 'UPCOMING')}</span>
                      <span className="text-[10px] font-bold text-theme-text-sec uppercase tracking-tight">{match.title || match.short_title || "T20 Match"}</span>
                    </div>
                    <div className="flex font-bold text-theme-text text-sm items-center w-full justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg overflow-hidden bg-theme-bg border border-theme-border shadow-theme-sm"><img src={match.team1_logo || getTeamLogo(match.team1)} className="w-full h-full object-contain" alt="" /></div>
                        <span className="uppercase tracking-tight">{match.team1_short || match.team1?.substring(0, 3)}</span>
                      </div>
                      <span className="text-[9px] font-bold text-theme-text-sec opacity-40 uppercase tracking-widest">VS</span>
                      <div className="flex items-center gap-2 flex-row-reverse">
                        <div className="w-7 h-7 rounded-lg overflow-hidden bg-theme-bg border border-theme-border shadow-theme-sm"><img src={match.team2_logo || getTeamLogo(match.team2)} className="w-full h-full object-contain" alt="" /></div>
                        <span className="uppercase tracking-tight">{match.team2_short || match.team2?.substring(0, 3)}</span>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-theme-border w-full flex justify-between items-center opacity-60">
                       <p className="text-[8px] font-bold text-theme-text-sec uppercase tracking-widest">Starts At</p>
                       <p className="text-[9px] font-bold text-accent uppercase tracking-wider">{parseMatchTime(match).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}</p>
                    </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showPlayerModal && (
        <div className="fixed inset-0 z-[100] flex justify-center items-end bg-theme-bg/80 backdrop-blur-sm p-0">
          <div className="bg-theme-card w-full max-h-[85vh] rounded-t-[32px] overflow-hidden shadow-theme-lg border-t-4 border-accent animate-[slideUp_0.3s_ease-out] flex flex-col">
            <div className="p-4 border-b border-theme-border flex items-center bg-theme-card sticky top-0 z-50 shrink-0">
               <button
                  onClick={() => { setShowPlayerModal(false); setSelectedPlayer(null); }}
                  className="w-10 h-10 flex items-center justify-center bg-theme-bg rounded-xl border border-theme-border text-theme-text transition-all active:scale-95 shadow-theme-sm mr-4"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] text-theme-text-sec font-bold uppercase tracking-widest opacity-60 block">{selectedCategory?.label} • {selectedMatch?.team1_short} vs {selectedMatch?.team2_short}</span>
                  {selectedMatch?.lineup_announced && (
                    <span className="bg-green-500/10 text-green-600 text-[8px] px-1.5 py-0.5 rounded font-bold uppercase tracking-widest border border-green-500/20">Lineups</span>
                  )}
                </div>
                <h3 className="text-sm font-bold text-theme-text uppercase tracking-tight">Pick Player</h3>
              </div>
            </div>


            {/* 🏃 ROLE TABS */}
            <div className="flex bg-theme-bg mx-4 mt-4 p-1 rounded-xl border border-theme-border shrink-0">
              {ROLES.map((role) => {
                const count = (players || []).filter(p => {
                    const r = (p.role || "").toUpperCase();
                    if (role.id === "AR") return r.includes("AR") || r.includes("ALL");
                    if (role.id === "WK") return r.includes("WK") || r.includes("WICKET");
                    if (role.id === "BAT") return r.includes("BAT");
                    if (role.id === "BOWL") return r.includes("BOWL");
                    return r === role.id;
                }).length;
                return (
                  <button
                    key={role.id}
                    onClick={() => setActiveRole(role.id)}
                    className={`flex-1 py-2 text-[10px] font-bold rounded-lg uppercase tracking-tight transition-all active:scale-95 ${activeRole === role.id
                      ? "bg-accent text-white shadow-sm"
                      : "text-theme-text-sec opacity-60"
                      }`}
                  >
                    {role.label}
                    <span className="ml-1 opacity-50">({count})</span>
                  </button>
                );
              })}
            </div>

            <div className="p-4 overflow-y-auto flex-1 space-y-3">
              {loading ? (
                <div className="text-center py-10 text-[10px] font-bold text-theme-text-sec uppercase tracking-widest">Loading Players...</div>
              ) : filteredPlayers.length === 0 ? (
                <div className="text-center py-10 text-[10px] font-bold text-theme-text-sec uppercase tracking-widest">No Players Found</div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {filteredPlayers.map(player => (
                    <div
                      key={player.player_id}
                      onClick={() => handlePlayerSelect(player)}
                      className={`flex items-center p-3 rounded-xl border transition cursor-pointer shadow-theme-sm ${selectedMatch?.lineup_announced && !player.is_playing
                        ? 'border-theme-border bg-theme-card opacity-40 grayscale'
                        : 'border-theme-border bg-theme-card active:bg-theme-bg active:scale-[0.98]'
                        }`}
                    >
                      <div className="relative shrink-0 mr-4">
                        <div className="w-11 h-11 rounded-xl bg-theme-bg border border-theme-border overflow-hidden">
                          <img src={player.image || '/player_placeholder.png'} alt={player.player_name} className="w-full h-full object-cover" />
                        </div>
                        {selectedMatch?.lineup_announced && (
                          <div className={`absolute -right-1 -bottom-1 w-3.5 h-3.5 rounded-full border-2 border-theme-card ${player.is_playing ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-theme-text text-sm uppercase tracking-tight truncate">{player.player_name}</h4>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[9px] text-accent font-bold uppercase tracking-widest leading-none">{player.role === 'AR' ? 'ALL' : player.role}</span>
                          <span className="w-1 h-1 bg-theme-border rounded-full"></span>
                          <span className="text-[9px] text-theme-text-sec font-bold uppercase tracking-widest truncate">{player.team || 'Unknown'}</span>
                        </div>
                      </div>
                      <div className="shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-theme-bg border border-theme-border">
                         <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-accent opacity-40"><path d="M5 12h14m-7-7l7 7-7 7"/></svg>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <BottomNav activeTab="" />
      <div className="text-center py-4 opacity-30 text-[8px] font-black uppercase tracking-[0.2em] pointer-events-none">Build v1.0.5</div>
      <style dangerouslySetInnerHTML={{ __html: `@keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }` }} />
    </div>
  );
};

export default PlayerPerformance;

